import { assertVal, getter } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Colors } from '../../../ui';
import { resolve } from '../../Container';
import { AdapterInfo } from '../AdapterInfo';
import { Camera } from '../Camera';
import { deleteSelection } from '../commands/SelectionCommands';
import { EditorModeInfo } from '../EditorModeInfo';
import { EditorSession } from '../EditorSession';
import { Physical, RelativeTo } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { InteractionHelper } from '../InteractionHelper';
import { Selection, SelectionSet } from '../Selection';
import { SpatialIndex } from '../utilities/SpatialIndex';
import { DesignMode } from '../workflow/DesignMode';
import { KeyMappings } from './KeyMappings';
import { ToolBehavior } from './ToolBehavior';
import { ToolOverlay } from './ToolOverlay';

type SelectionRegion = { from?: Point, to?: Point };
type SelectAction = 'set' | 'setOrClear' | 'toggle' | 'appendInLayer' | 'appendInView';

export class SelectionToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(InteractionHelper) private interaction!: InteractionHelper;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(SpatialIndex) private spatialIndex!: SpatialIndex;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;

    private region = {} as SelectionRegion;
    private maybePerformSelectOnMouseUp = false;
    private mouseDownAt = null as Point;

    public getKeyMappings = getter(KeyMappings.from(
        ['BACKSPACE', [deleteSelection, [true]]],
        ['DELETE', [deleteSelection, [true]]],
    ));

    public init() {
        this.overlay.add(this.paintRegion.bind(this));
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        const { camera, session } = this;

        if (mi.button !== MouseButtons.Main) return;
        if (state !== 'idle') return;

        // If we are selecting an overlay target, don't affect selection.
        if (!!this.interaction.getTopAsOverlayTarget()) return;

        this.mouseDownAt = mi.position;

        const action = this.getActionForMouseDown(mi);

        if (action === 'drag') {
            this.maybePerformSelectOnMouseUp = true;
            const mpt = camera.toModelPoint(mi.position);
            this.updateRegion({ from: mpt });
            return 'selecting';
        }
        else if (action === 'set' && this.isPointInTransform(mi.position) && this.isNetVisible()) {
            this.maybePerformSelectOnMouseUp = true;
        }
        else {
            const next = this.getNextSelectionForAction(action, false);
            if (next) {
                this.session.update(Selection, next);
            }
            else {
                const isAdapterActive = session.get(AdapterInfo) !== AdapterInfo.empty && session.get(AdapterInfo).type !== 'autoLayout';
                if (isAdapterActive) {
                    this.session.update(AdapterInfo, AdapterInfo.empty);
                }
                this.maybePerformSelectOnMouseUp = !isAdapterActive;
            }
        }
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        const { camera } = this;

        if (state === 'selecting') {
            assertVal(this.region, 'SelectionToolBehavior: selecting without region?');
            this.updateRegion({ to: camera.toModelPoint(mi.position) });
        }
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        const { dispatch, region } = this;

        if (mi.button !== MouseButtons.Main) return;
        if (!this.maybePerformSelectOnMouseUp && state !== 'selecting') return;

        const dragDistance = mi.position.distanceTo(this.mouseDownAt);
        this.mouseDownAt = null;

        if (this.maybePerformSelectOnMouseUp) {
            if (mi.count === 1 && dragDistance <= 2) {
                const mode = (mi.modifiers.ctrl || mi.modifiers.shift) ? 'toggle' : 'setOrClear';
                const force = this.session.get(EditorModeInfo).id === DesignMode.id;
                const next = this.getNextSelectionForAction(mode, force);
                if (next) {
                    this.session.update(Selection, next);
                }
            }

            this.maybePerformSelectOnMouseUp = false;
        }

        if (state === 'selecting') {
            assertVal(region, 'SelectionToolBehavior: selecting without region?');
            const { from, to } = region;

            if (dragDistance > 2 && !!from && !!to) {
                const next = this.getNextRegionSelection(region, false);
                this.session.update(Selection, next);
            }

            this.updateRegion(null);
            return 'idle';
        }
    }

    private getActionForMouseDown(mi: MouseInput) {

        const double = mi.count === 2;
        const triple = mi.count >= 3;

        if (mi.modifiers.shift && triple) {
            return 'appendInView';
        }
        if (mi.modifiers.shift && double) {
            return 'appendInLayer';
        }
        if (mi.modifiers.shift) {
            return 'drag';
        }
        if (mi.modifiers.ctrl) {
            return 'toggle';
        }

        return 'set';
    }

    private updateRegion(value: Partial<SelectionRegion>) {
        this.region = !!value ? { ...this.region, ...value } : {};
        this.overlay.repaint();
    }

    private paintRegion(gfx: UniversalSurfaceRenderer) {
        const { from, to } = this.region ?? {};
        if (!from || !to) return;

        const rect = Rect.fromPoints([
            this.camera.toView(from),
            this.camera.toView(to),
        ]);

        gfx.lineWidth = 1.5;
        // gfx.lineDashOffset = this.ticker++;
        // gfx.setLineDash([6, 4]);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.strokeRect(rect.left, rect.top, rect.width, rect.height);
    }

    private getNextSelectionForAction(action: SelectAction, includeLocked: boolean) {

        const curr = this.session.get(Selection);
        const [target] = this.interaction.testFor('select', includeLocked);

        if (action === 'set' && !!target && !curr.has(target)) {
            // Clear selection and if hit, add as only item
            return SelectionSet.create([target]);
        }

        if (action === 'setOrClear' && !curr.has(target)) {
            return !!target
                ? SelectionSet.create([target])
                : SelectionSet.empty;
        }

        if (action === 'toggle' && !!target) {
            // Either select or unselect depending on current state
            return curr.has(target)
                ? curr.delete(target)
                : curr.add(target);
        }

        if (target && (action === 'appendInLayer' || action === 'appendInView')) {
            const frame = this.camera.getModelFrame();
            const layer = target.parentPhysical;
            const desc = Component.describe(target);
            const candidates = (action === 'appendInLayer')
                ? layer.polarize(desc.type)
                : Physicals.polarize(this.spatialIndex.lookup(frame), desc.type);
            const accepted = candidates.filter(x =>
                (!x.locked || includeLocked) &&
                x.bounds(RelativeTo.Model).intersects(frame));
            return curr.addMany(accepted);
        }

        return null;
    }

    private getNextRegionSelection(region: SelectionRegion, includeLocked: boolean) {

        const curr = this.session.get(Selection);
        const area = Rect.fromPoints([region.from, region.to]);
        const candidates = this.spatialIndex.lookup(area);
        const inBoundry = (p: Physical) => p.parentPhysical?.boundary || (!!p.parentPhysical && inBoundry(p.parentPhysical));

        const capture = new Set<Physical>();
        for (const c of candidates) {
            // Ignore if locked if not included
            if (c.locked && !includeLocked) continue;
            // Ignore if in boundary
            if (inBoundry(c)) continue;
            // Ignore if position is not in region
            if (!area.contains(c.position(RelativeTo.Model))) continue;
            // Ignore if all points on path are not in region
            if (c.outline(RelativeTo.Model).points.find(x => !area.contains(x))) continue;
            capture.add(c);
        }

        return curr.addMany(capture);
    }

    private isPointInTransform(pt: Point) {
        const mpt = this.camera.toModel(pt);
        const elmts = this.session.get(Selection).toArray();
        const bounds = Physicals.bounds(elmts);
        return bounds.contains(mpt);
    }

    private isNetVisible() {
        // Hack city:
        const elmts = this.session.get(Selection).toArray();
        return elmts.some(x => x.queryTransformHints(elmts.length === 1).visible);
    }
}
